import * as React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GridColumn } from "semantic-ui-react";
import { definedRouteTypes } from "api/api";
import { useLocation } from "react-router-dom";
import CompanyListCard from "components/molecules/Cards/CompanyListCard";
import MediaLinkCard from "components/molecules/Cards/MediaLinkCard";
import MediaSourceCard from "components/molecules/Cards/MediaSourceCard";
import { INewsDisplay } from "api/types/NewsEntity";
import { IBusinessEntityDisplay } from "api/types/BusinessEntity";
import { ILinkEntityDisplay } from "api/types/LinkEntity";

import { DEFAULT_LIMIT } from "../../../constants/important";
import {
  StyledResultText,
  StyledDivider,
  StyledPaginator,
} from "../Menus/QueryMenu/FilterMenuStyles";
import DynamicCardGrid from "../../templates/DynamicCardGrid";
import theme from "../../../styles/theme";
import EventGridCard, {
  definedCardTypes,
  definedCardSizes,
} from "../../molecules/Cards/EventGridCard";
import Paginator from "../Paginator/Paginator";
import JoinUsBanner from "../../molecules/InfoBanners/JoinUsBanner";
import Divider from "../../atoms/Divider/Divider";
import NoResultsBanner from "../../molecules/InfoBanners/NoResultsBanner";
import OrganizationListCard from "../../molecules/Cards/OrganizationListCard";
import DynamicCardList from "../../templates/DynamicCardList";
import ErrorBanner from "../../molecules/InfoBanners/ErrorBanner";
import MarkerMap from "../EventsMap/MarkerMap";

export interface IQueryResult {
  handleNextPage(): void;
  handlePrevPage(): void;
  loading: boolean;
  error: boolean;
  validQuery: boolean;
  events: INewsDisplay[];
  count: number;
  query?: string;
  category?: string[];
  countries?: string[];
  keywords?: string[];
  disableNextPage: boolean;
  disablePrevPage: boolean;
  showAllRecords?: boolean;
}

export interface QueryResultProps {
  queryResult: IQueryResult;
  fullTextSearch?: boolean;
  count?: boolean;
  cardType?: definedCardTypes;
  newTab?: boolean;
  columns?: 1 | 2 | 3 | 4;
  removeLabels?: boolean;
}

const StyledAccents = styled.span`
  &&& {
    color: ${theme.palette.primary.main};
  }
`;

interface IRouterState {
  map?: boolean;
}

const QueryResult: React.FC<QueryResultProps> = ({
  queryResult,
  count: showCount,
  cardType,
  columns,
  newTab,
  fullTextSearch,
  removeLabels,
}) => {
  const {
    handleNextPage,
    handlePrevPage,
    loading,
    error,
    validQuery,
    events,
    count,
    query,
    disableNextPage,
    disablePrevPage,
    showAllRecords,
  } = queryResult;
  const { t } = useTranslation();

  const location = useLocation<IRouterState>();
  const isMapOpen = location.state?.map;

  let CardGrid = DynamicCardGrid;
  if (cardType === definedCardTypes.organization) {
    CardGrid = DynamicCardList;
  }

  const columnCount =
    columns || (cardType === definedCardTypes.organization ? 1 : 4);
  const rowCount = 12 / columnCount;

  if (error) {
    return <ErrorBanner />;
  }
  if (isMapOpen) {
    return <MarkerMap events={events} />;
  }

  return (
    <>
      {validQuery && query && showCount && (
        <>
          <StyledResultText>
            {t("Filter.Found")}
            <StyledAccents>{t("Filter.EventAmount", { count })}</StyledAccents>
          </StyledResultText>
          <StyledDivider>
            <Divider />
          </StyledDivider>
        </>
      )}
      {((validQuery && events.length > 0) || loading) && (
        <>
          <CardGrid
            loading={loading}
            columns={columnCount}
            rows={rowCount}
            gridTag="recentGrid"
            type={cardType}
          >
            {events.map((card) => {
              const { slug, type: recordType } = card;
              if (
                cardType === definedCardTypes.organization &&
                recordType === definedRouteTypes.organizations
              ) {
                const displayCard = (card as unknown) as IBusinessEntityDisplay;
                return (
                  <React.Fragment key={card.slug}>
                    <GridColumn>
                      <OrganizationListCard organization={displayCard} />
                    </GridColumn>
                    <Divider />
                  </React.Fragment>
                );
              }
              if (
                (cardType === definedCardTypes.organization &&
                  recordType === definedRouteTypes.companies) ||
                recordType === definedRouteTypes.selfEmployed
              ) {
                const displayCard = (card as unknown) as IBusinessEntityDisplay;
                return (
                  <React.Fragment key={card.slug}>
                    <GridColumn>
                      <CompanyListCard organization={displayCard} />
                    </GridColumn>
                    <Divider />
                  </React.Fragment>
                );
              }
              if (recordType === definedRouteTypes.mediaSources) {
                return (
                  <GridColumn key={slug}>
                    <MediaSourceCard data={card} />
                  </GridColumn>
                );
              }
              if (recordType === definedRouteTypes.mediaLinks) {
                return (
                  <GridColumn key={slug}>
                    <MediaLinkCard card={card as ILinkEntityDisplay} />
                  </GridColumn>
                );
              }
              let newType = definedCardTypes.post;
              if (recordType.includes("notikumi")) {
                newType = definedCardTypes.calendar;
              }

              return (
                <GridColumn key={slug}>
                  <EventGridCard
                    removeLabels={removeLabels}
                    newTab={newTab}
                    size={definedCardSizes.medium}
                    event={card}
                    type={newType || definedCardTypes.post}
                    key={slug}
                  />
                </GridColumn>
              );
            })}
          </CardGrid>
          {events.length > DEFAULT_LIMIT ||
            (showAllRecords && (
              <StyledPaginator>
                <Paginator
                  disableNext={disableNextPage}
                  disablePrev={disablePrevPage}
                  handleNext={handleNextPage}
                  handlePrev={handlePrevPage}
                />
              </StyledPaginator>
            ))}
        </>
      )}
      {validQuery && events.length === 0 && !loading && (
        <>
          {fullTextSearch && <NoResultsBanner query={query} />}
          <JoinUsBanner />
        </>
      )}
    </>
  );
};

export default QueryResult;
